import React, { useEffect, useState } from "react";
import { Form, Button, Row, Col } from "react-bootstrap";
import { LeaveTransaction } from "../types/LeaveTransaction";
import { validateEmployeeId } from "../config/functions";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../redux/store";
import { fetchLeaveRequests } from "../redux/actions/leaveRequestAction";
import { StoreState } from "../redux/reducers";
import "../styles/Form.css";
import EmployeeSalaryFilter from "./EmployeeSalaryFilter";
import Select from "react-select";
import { fetchEmployeesAction } from "../redux/actions/employeeActions";

interface Props {
  initialData?: LeaveTransaction | null;
  onSave: (data: LeaveTransaction) => void;
}

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Updated: Convert full month name to 2-digit format
const fullToShortMonth = (fullMonth: string): string => {
  const index = monthNames.findIndex(
    (name) => name.toLowerCase() === fullMonth.toLowerCase(),
  );
  return index >= 0 ? String(index + 1).padStart(2, "0") : fullMonth;
};

// For showing month name from numeric or short format in dropdown
const convertInitialMonth = (rawMonth: string | number | undefined): string => {
  if (!rawMonth) return "";
  const raw = rawMonth.toString().toLowerCase();

  if (!isNaN(Number(raw))) {
    const index = Number(raw) - 1;
    return monthNames[index] || "";
  }

  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const LeaveTransactionForm: React.FC<Props> = ({ initialData, onSave }) => {
  const dispatch = useDispatch<AppDispatch>();
  const leaveRequests = useSelector((state: StoreState) => state.leaveRequest);
  const employee = useSelector((state: StoreState) => state.employee);  
  const [employeeIdValid, setEmployeeIdValid] = useState<boolean | null>(null);

  const [formData, setFormData] = useState<LeaveTransaction>({
    id: initialData?.id ?? 0,
    employeeId: initialData?.employeeId ?? "",
    year: initialData?.year ?? "",
    month: convertInitialMonth(initialData?.month),
    leaveType: initialData?.leaveType ?? "",
    credit: initialData?.credit ?? 0,
    debit: initialData?.debit ?? 0,
    leaveRequestId: initialData?.leaveRequestId ?? 0,
    balance: initialData?.leaveRequestId ?? 0,
    createTimestamp: initialData?.createTimestamp ?? "",
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value)) {
      let cleanValue = value.replace(/^0+/, "");
      setFormData({ ...formData, [name]: cleanValue === "" ? "" : cleanValue });
    }
    if (name === "employeeId") {
      validateEmployeeId(value).then(setEmployeeIdValid);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formattedData = {
      ...formData,
      month: fullToShortMonth(formData.month), // convert to "03" format
    };
    onSave(formattedData);
  };

  useEffect(() => {
    dispatch(fetchLeaveRequests());
    dispatch(fetchEmployeesAction());
  }, [dispatch]);

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mb-1">
        <Col className="form-column">
          <Form.Group controlId="employeeId">
            <Form.Label>Employee ID</Form.Label>
            <Select
              options={employee.map((emp) => ({
                label: `${emp.employeeId}`,
                value: emp.id.toString(),
              }))}
              placeholder="Select Employee Id"
              isMulti={false}
              value={
                employee
                  .map((emp) => ({
                    label: `${emp.employeeId}`,
                    value: emp.id.toString(),
                  }))
                  .find((opt) => opt.label === formData.employeeId) || null
              }
              onChange={(selected: any) => {
                setFormData({
                  ...formData,
                  employeeId: selected?.label || "",
                });
              }}
            />
          </Form.Group>
        </Col>

        <Col className="form-column">
          {/* <Form.Group controlId="year">
            <Form.Label>Year</Form.Label>
            <Form.Control
              type="text"
              name="year"
              value={formData.year}
              onChange={handleChange}
              required
            />
          </Form.Group> */}
          <Form.Group controlId="year">
            <Form.Label>
              Year <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="year"
              value={formData.year}
              onChange={handleChange}
              placeholder="year"
              required
            >
              <option value="">Select Year</option>
              {Array.from({ length: 11 }, (_, i) => {
                const y = new Date().getFullYear() - 5 + i;
                return (
                  <option key={y} value={y}>
                    {y}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col className="form-column">
          <Form.Group controlId="month">
            <Form.Label>Month</Form.Label>
            <Form.Select
              name="month"
              value={formData.month}
              onChange={handleChange}
              required
            >
              <option value="">Select Month</option>
              {monthNames.map((month) => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-1">
        <Col className="form-column">
          {/* <Form.Group controlId="leaveType">
            <Form.Label>Leave Type</Form.Label>
            <Form.Control
              type="text"
              name="leaveType"
              value={formData.leaveType}
              onChange={handleChange}
              required
            />
          </Form.Group> */}
          <Form.Group controlId="leaveType">
            <Form.Label>
              LeaveType <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              name="leaveType"
              value={formData.leaveType}
              onChange={handleChange}
              placeholder="leaveType"
              required
            >
              <option value="">Select leaveTyp</option>
              <option value="pl">PL</option>
              <option value="el">EL</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col className="form-column">
          <Form.Group controlId="credit">
            <Form.Label>Credit</Form.Label>
            <Form.Control
              type="text"
              name="credit"
              value={formData.credit}
              onChange={handleChange}
              inputMode="numeric"
              required
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col className="d-flex justify-content-end mt-4">
          <Button
            type="submit"
            className="btn-save align-end"
            disabled={employeeIdValid === false}
          >
            {initialData ? "Update Leave" : "Add Leave"}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default LeaveTransactionForm;
